import {PropertyFilterOperator, useCollection} from '@amzn/awsui-collection-hooks'
import {
    Box,
    NonCancelableCustomEvent,
    Pagination,
    PaginationProps,
    PropertyFilter,
    PropertyFilterProps,
    Table,
    TableProps,
} from '@amzn/awsui-components-react'
import React, {FunctionComponent, useRef} from 'react'
import '../css/table.css'
import {getBlobUrl, getHeaders, tableHeader} from './TableHeaderTemplate'

export interface TableInput {
    tableItems: any[]
    additionalValue?: any
    fileName: string
}

export interface TableDetails {
    tableId: string
    header: string
    fileName: string
    getRow: { (row: any): string[] }
}

export interface LazyLoadPaginationProps {
    currentPageIndex: number,
    onNextPageClick: ((event: NonCancelableCustomEvent<PaginationProps.PageClickDetail>) => void),
    pagesCount: number,
    disabled: boolean,
    openEnd: boolean,
}

/**
 * Custom hook function that uses Polaris's 'useCollection' hook to create client-side table.
 */
export const useTableCreation = (
    tableDetails: TableDetails,
    columnDefinitions: TableProps.ColumnDefinition<any>[],
    tableItems: any[],
    filteringProperties: PropertyFilterProps.FilteringProperty[],
    defaultSortColumn: number = 0,
    lazyLoadPaginationProps?: LazyLoadPaginationProps,
    AdditionalAction?: FunctionComponent,
) => {
    const csvInstance = useRef<any>(null)

    const {items, filteredItemsCount, collectionProps, propertyFilterProps, paginationProps} = useCollection(
        tableItems,
        {
            propertyFiltering: {
                filteringProperties: filteringProperties,
                empty: emptyTableDiv,
                noMatch: emptyTableDiv,
            },
            pagination: {
                pageSize: 10,
            },
            sorting: {
                defaultState: {
                    sortingColumn: columnDefinitions[defaultSortColumn],
                },
            },
        },
    )

    function getData(): string[][] {
        const header = getHeaders(columnDefinitions)
        return header.concat(tableItems.map(item => {
            return tableDetails.getRow(item)
        }))
    }

    function onClickDownload() {
        if (csvInstance && csvInstance.current) {
            setTimeout(() => {
                csvInstance.current.click()
            })
        }
    }

    function onClickViewAll() {
        window.open(getBlobUrl(getData(), 'text/plain'))
    }

    const getHeaderDetails = () => {
        return {
            header: tableDetails.header,
            fileName: tableDetails.fileName,
            data: getData(),
            csvInstance: csvInstance,
            downloadButtonDetails: {onClickDownload: onClickDownload, tooltip: 'Download', tooltipPlacement: 'top'},
            onClickViewAll: onClickViewAll,
            AdditionalAction: AdditionalAction,
        }
    }

    function generatePaginationComponent() {
        const props = lazyLoadPaginationProps ? {...lazyLoadPaginationProps, ...paginationProps} : {...paginationProps, openEnd: true}

        return (
            <Pagination
                {...props}
            />
        )
    }

    return (
        <Table
            id={tableDetails.tableId}
            header={tableHeader(getHeaderDetails())}
            stickyHeader
            columnDefinitions={columnDefinitions}
            items={items}
            {...collectionProps}
            pagination={generatePaginationComponent()}
            filter={
                <PropertyFilter
                    {...propertyFilterProps}
                    filteringOptions={[]}
                    i18nStrings={PROPERTY_FILTERING_I18N_CONSTANTS}
                    countText={getFilterCounterText(filteredItemsCount || 0)}
                />
            }
        />
    )
}

export const emptyTableDiv = (
    <Box variant="p">None</Box>
)

export const getFilterCounterText = (count: number) => `${count} ${count === 1 ? 'match' : 'matches'}`
export const operators = [':', '!:', '=', '!='] as PropertyFilterOperator[]

/**
 * Localized strings required by PropertyFilter in Polaris's table component.
 */
export const PROPERTY_FILTERING_I18N_CONSTANTS = {
    filteringPlaceholder: 'Search',
    filteringAriaLabel: 'your choice',
    dismissAriaLabel: 'Dismiss',
    groupValuesText: 'Values',
    groupPropertiesText: 'Properties',

    operatorsText: 'Operators',
    operationAndText: 'and',
    operationOrText: 'or',
    operatorLessText: 'Less than',
    operatorLessOrEqualText: 'Less than or equal',
    operatorGreaterText: 'Greater than',
    operatorGreaterOrEqualText: 'Greater than or equal',
    operatorContainsText: 'Contains',
    operatorDoesNotContainText: 'Does not contain',
    operatorEqualsText: 'Equals',
    operatorDoesNotEqualText: 'Does not equal',

    editTokenHeader: 'Edit filter',
    propertyText: 'Property',
    operatorText: 'Operator',
    valueText: 'Value',
    cancelActionText: 'Cancel',
    applyActionText: 'Apply',
    allPropertiesLabel: 'All properties',

    tokenLimitShowMore: 'Show more',
    tokenLimitShowFewer: 'Show fewer',
    clearFiltersText: 'Clear filters',
    removeTokenButtonAriaLabel: () => 'Remove token',
    enteredTextLabel: (text: any) => `"${text}"`,
}
